'use client';

import { requestLogin } from '@/lib/auth/login-dialog';
import navigationStyles from './AccountNavigation.module.css';
import navigationMotion from './GlobalNavigationMotion.module.css';
import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';
import { StudioAction } from '@/components/ui/StudioAction';

import { SystemLoadingIcon as Loader2, SystemLoadingSwitch } from '@/components/ui/SystemLoading';
import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo, useTransition } from 'react';
import { createPortal } from 'react-dom';
import { BrandIcon } from '@/components/common/BrandIcon';
import { DobiAssetsIcon, DobiExploreIcon, DobiProjectsIcon, DOBI_CREATION_ICONS } from '@/components/common/DobiSymbol';
import { usePathname, useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Plus, Bell, X, Pencil, Check, ChevronDown, ChevronLeft, ChevronRight, Home, Inbox, MessageCircle, Mail, RefreshCw, UsersRound, Folder, FolderPlus, MoreHorizontal, Trash2, Tag } from 'lucide-react';
import { cn } from '@/lib/cn';
import { exploreVideoScheduler } from '@/lib/explore-video-scheduler';
import dynamic from 'next/dynamic';
import { useProjectStore } from '@/stores/projectStore';
import type { ProjectCategoryRecord, ProjectRecord } from '@/stores/projectStore';
import { projectApi } from '@/lib/api/projects';
import { inboxApi, readSidebarInbox } from '@/lib/api/inbox';
import { resizeImageUrl } from '@/lib/image-utils';
import { LazyImage } from '@/components/ui/LazyImage';
import { delosTransition } from '@/lib/motion';
import { useLocale, useTranslations } from 'next-intl';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { UserAvatarMenu } from '@/components/layout/UserAvatarMenu';
import { pricingModal } from '@/lib/stores/pricing-modal-store';
import { creatorReferralsModal } from '@/lib/stores/creator-referrals-modal-store';
import { systemDialog } from '@/lib/stores/system-dialog-store';
import { BrandWordmark } from '@/components/common/BrandWordmark';
import { getExploreHref, type ExploreTopTab } from '@/components/explore/exploreNavigation';
import { GlobalNavigationRail, RailButton } from './GlobalNavigationRail';
import { NavigationRailMenu, NAVIGATION_MENU_ITEM_CLASS_NAME } from './NavigationRailMenu';
import { PricingNavigationButton } from './PricingNavigationButton';
import { useGlobalNavigationStore } from '@/lib/stores/global-navigation-store';
import { getStudioToolHref } from '@/components/tools/catalog';
import type { QuickStartKey } from '@/types/quick-start';
import { EXPLORE_QUICK_START_ITEMS } from '@/components/explore/exploreQuickStart';
import { ExploreVirtualTryOnButton } from '@/components/explore/ExploreVirtualTryOnButton';
import {
  getTopbarIconSurfaceClassName,
  getTopbarToneClassName,
  TOPBAR_ICON_BUTTON_CLASSNAME,
  TOPBAR_ICON_CLASSNAME,
  TOPBAR_ICON_TOOLTIP_CLASSNAME,
  TopbarTextButton,
  TOPBAR_TEXT_BUTTON_CLASSNAME,
} from '@/components/ui/TopbarTextButton';
import { formatDefaultProjectNameDate } from '@/lib/projects/defaultProjectName';
import { useResponsiveLayout } from '@/lib/hooks/useResponsiveLayout';
import { useCreditsStore } from '@/lib/stores/credits-store';
import { WelcomeRewardMessage } from '@/components/credits/WelcomeReward';
import { buildLoginRedirectPath } from '@/lib/auth/session';
import { useAuthStore } from '@/stores/authStore';
import { getAuthScopeRevision } from '@/lib/auth-scoped-state-reset';
import type { InboxMessage } from '@/types/inbox';
import {
  prepareVisualEditorProjectChatSidebar,
  syncVisualEditorProjectName,
} from '@/components/layout/visualEditorProjectBridge';


export type SidebarNavItem = 'explore' | 'publishing' | 'admin' | 'director-board' | 'trash' | null;

// Canvas stores/history are only needed on a canvas, not every shared link.
const EnterpriseContactDialog = dynamic(() => import('@/components/public-site/EnterpriseContactDialog').then(module => module.EnterpriseContactDialog));

const WorkspaceNavigation = dynamic(() => import('./WorkspaceNavigation').then((module) => module.WorkspaceNavigation));

const QUICK_START_ICONS = { ...DOBI_CREATION_ICONS, cinemaStudio: Camera };

function SidebarVirtualTryOnButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <ExploreVirtualTryOnButton {...props} resumeFromUrl={false} />;
}

type IconComponent = React.ComponentType<{ className?: string; strokeWidth?: number }>;
type AssistantGenerationStatusState = 'idle' | 'processing' | 'completed';
type MobileUtilityMenuView = 'root' | 'notifications';
const PROJECT_MENU_PAGE_SIZE = 24;

function SidebarThumbnailImage({
  sourceUrl,
  targetWidth,
  quality,
  displaySize,
}: {
  sourceUrl: string;
  targetWidth: number;
  quality: number;
  displaySize: number;
}) {
  const src = resizeImageUrl(sourceUrl, targetWidth, quality);
  return (
    <LazyImage
      src={src}
      alt=""
      sizes={`${displaySize}px`}
      loading="lazy"
      className="h-full w-full"
      imgClassName="h-full w-full object-cover"
      fallbackIconClass="h-4 w-4"
    />
  );
}

type SidebarIdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

function scheduleSidebarIdleWork(callback: () => void, delay = 12000): () => void {
  const idleWindow = window as SidebarIdleWindow;
  let idleHandle: number | undefined;
  const delayHandle = window.setTimeout(() => {
    if (typeof idleWindow.requestIdleCallback === 'function') {
      idleHandle = idleWindow.requestIdleCallback(callback, { timeout: 1500 });
      return;
    }
    callback();
  }, delay);
  return () => {
    window.clearTimeout(delayHandle);
    if (idleHandle !== undefined) {
      idleWindow.cancelIdleCallback?.(idleHandle);
    }
  };
}

export interface AssistantGenerationStatus {
  state: AssistantGenerationStatusState;
  activeCount: number;
  unreadCompletedCount: number;
}

interface AppSidebarProps {
  toolsNavigation?: { active: QuickStartKey | 'explore' | 'assets' };
  activeProjectId?: string | null;
  activeNav: SidebarNavItem;
  exploreTopTab: ExploreTopTab;
  showProjectShortcut?: boolean;
  assistantControl?: {
    label: string;
    active?: boolean;
    tone?: 'neutral' | 'accent-outline' | 'accent-solid';
    onClick: () => void;
  };
  onExploreTopTabChange: (tab: ExploreTopTab) => void;
  onNavChange: (nav: SidebarNavItem) => void;
  onProjectSelect: (projectId: string) => void;
}

export function AppSidebar({
  toolsNavigation,
  activeProjectId,
  activeNav,
  exploreTopTab,
  assistantControl,
  onExploreTopTabChange,
  onNavChange,
  onProjectSelect,
}: AppSidebarProps) {
  const projects = useProjectStore((s) => s.projects);
  const projectCategories = useProjectStore((s) => s.projectCategories);
  const collaboratedProjects = useProjectStore((s) => s.collaboratedProjects);
  const projectsLoading = useProjectStore((s) => s.loading);
  const projectsError = useProjectStore((s) => s.error);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const fetchProjectCategories = useProjectStore((s) => s.fetchProjectCategories);
  const fetchCollaboratedProjects = useProjectStore((s) => s.fetchCollaboratedProjects);
  const createCategory = useProjectStore((s) => s.createCategory);
  const updateCategory = useProjectStore((s) => s.updateCategory);
  const deleteCategory = useProjectStore((s) => s.deleteCategory);
  const moveProjectToCategory = useProjectStore((s) => s.moveProjectToCategory);
  const removeProject = useProjectStore((s) => s.removeProject);
  const t = useTranslations('nav');
  const locale = useLocale();
  const authT = useTranslations('auth');
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authUserId = useAuthStore((state) => state.user?.id ?? null);
  const canLoadUserData = isAuthenticated && Boolean(authUserId);
  const commonT = useTranslations('common');
  const exploreT = useTranslations('explore');
  const workspaceT = useTranslations('workspace');
  const { isMobile } = useResponsiveLayout();
  const pathname = usePathname();
  const isCanvasRoute = Boolean(pathname?.startsWith('/p/'));
  const shouldFetchCollaboratedProjects = Boolean(activeProjectId) && !pathname?.startsWith('/admin');
  const isProjectView = activeNav === null && !!activeProjectId;
  const navigationCollapsed = useGlobalNavigationStore(state => state.collapsed);
  const router = useRouter();
  const [navigationPending, startNavigation] = useTransition();
  const prefetchedToolHrefs = useRef(new Set<string>());
  const [pendingTool, setPendingTool] = useState<QuickStartKey | null>(null);
  useEffect(() => {
    if (navigationPending || pendingTool !== null) return exploreVideoScheduler.suspend();
  }, [navigationPending, pendingTool]);
  const prefetchTool = useCallback((href: string) => {
    if (href === pathname || prefetchedToolHrefs.current.has(href)) return;
    prefetchedToolHrefs.current.add(href);
    try {
      router.prefetch(href);
    } catch {
      // Speculative work must not prevent the actual navigation or a later retry.
      prefetchedToolHrefs.current.delete(href);
    }
  }, [pathname, router]);
  useEffect(() => { setPendingTool(null); }, [pathname]);
  const railT = useTranslations('globalNavigation');
  const prefetchDestination = (href: string) => {
    // Same-workspace tabs need no RSC tree at all. Prefetch only a real route change.
    if (window.location.pathname + window.location.search === href
      || (window.location.pathname === '/explore' && href.startsWith('/explore'))) return;
    router.prefetch(href);
  };
  const openTool = (href: string) => {
    if (window.location.pathname + window.location.search !== href) startNavigation(() => router.push(href));
  };
  const initCredits = useCreditsStore((s) => s.initCredits);

  const [isCreating, setIsCreating] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [inboxMessages, setInboxMessages] = useState<InboxMessage[]>([]);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxError, setInboxError] = useState<string | null>(null);
  const [claimingInboxMessageId, setClaimingInboxMessageId] = useState<string | null>(null);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [mobileUtilityMenuOpen, setMobileUtilityMenuOpen] = useState(false);
  const [mobileUtilityMenuView, setMobileUtilityMenuView] = useState<MobileUtilityMenuView>('root');
  const inboxRequestRef = useRef(0);
  useEffect(() => () => { inboxRequestRef.current += 1; }, []);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const projectMenuRef = useRef<HTMLDivElement>(null);
  const projectPopoverRef = useRef<HTMLDivElement>(null);
  const mobileUtilityMenuRef = useRef<HTMLDivElement>(null);

  const welcomePending = useCreditsStore((state) => state.credits?.welcome_credit_status === 'pending');
  const hasUnreadNotifications = inboxUnreadCount > 0 || welcomePending;
  const rewardT = useTranslations('welcomeReward');
  useEffect(() => {
    if (!canLoadUserData || isCanvasRoute) return;
    // Ordinary module switches reuse the shared snapshot; leaving a canvas invalidates its cover.
    void fetchProjects();
    fetchProjectCategories();
    if (shouldFetchCollaboratedProjects) {
      fetchCollaboratedProjects();
    }
  }, [canLoadUserData, fetchProjects, fetchProjectCategories, fetchCollaboratedProjects, isCanvasRoute, shouldFetchCollaboratedProjects]);

  useEffect(() => {
    if (!isCanvasRoute) return;
    return () => useProjectStore.getState().invalidateProjects();
  }, [isCanvasRoute]);

  useEffect(() => {
    if (!canLoadUserData || !projectMenuOpen) return;
    const refreshMenuData = () => {
      void fetchProjects(true);
      void fetchProjectCategories(true);
      if (shouldFetchCollaboratedProjects) {
        void fetchCollaboratedProjects();
      }
    };
    // Keep the cached list immediately usable. A quick project switch should
    // not race three sidebar refreshes against the next canvas's critical reads.
    if (!useProjectStore.getState().hasFetched) {
      refreshMenuData();
      return;
    }
    const refreshTimer = window.setTimeout(refreshMenuData, 1500);
    return () => window.clearTimeout(refreshTimer);
  }, [canLoadUserData, fetchCollaboratedProjects, fetchProjectCategories, fetchProjects, isCanvasRoute, projectMenuOpen, shouldFetchCollaboratedProjects]);

  useEffect(() => {
    if (!canLoadUserData) return;
    if (!isCanvasRoute) {
      void initCredits();
      return;
    }
    return scheduleSidebarIdleWork(() => {
      void initCredits();
    });
  }, [canLoadUserData, initCredits, isCanvasRoute]);

  const loadInboxMessages = useCallback(async (options?: { markRead?: boolean }) => {
    if (!canLoadUserData) return;
    const scope = getAuthScopeRevision();
    const request = ++inboxRequestRef.current;
    const isCurrent = () => scope === getAuthScopeRevision() && request === inboxRequestRef.current;
    setInboxLoading(true);
    setInboxError(null);
    try {
      const [response] = await Promise.all([
        readSidebarInbox(Boolean(options?.markRead)),
        options?.markRead ? useCreditsStore.getState().refetchCredits({ silent: true }) : Promise.resolve(),
      ]);
      if (!isCurrent()) return;
      if (response.error) throw response.error;
      setInboxMessages(response.data?.items ?? []);
      setInboxUnreadCount(response.data?.unread_count ?? 0);
      if (options?.markRead && (response.data?.unread_count ?? 0) > 0) {
        const readResponse = await inboxApi.markAllRead();
        if (!isCurrent()) return;
        if (readResponse.error) throw readResponse.error;
        setInboxUnreadCount(0);
        setInboxMessages((current) => current.map((message) => (
          message.status === 'unread'
            ? { ...message, status: 'read', read_at: message.read_at ?? new Date().toISOString() }
            : message
        )));
      }
    } catch (error) {
      if (isCurrent()) setInboxError(error instanceof Error ? error.message : t('inbox.loadFailed'));
    } finally {
      if (isCurrent()) setInboxLoading(false);
    }
  }, [canLoadUserData, t]);

  useEffect(() => {
    if (!canLoadUserData) {
      setInboxMessages([]);
      setInboxUnreadCount(0);
      setInboxError(null);
      return;
    }
    if (isCanvasRoute) return scheduleSidebarIdleWork(() => { void loadInboxMessages(); });
    void loadInboxMessages();
  }, [canLoadUserData, isCanvasRoute, loadInboxMessages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        setNotificationsOpen(false);
      }
      if (projectMenuRef.current && !projectMenuRef.current.contains(target)
        && !projectPopoverRef.current?.contains(target)) {
        setProjectMenuOpen(false);
      }
      if (mobileUtilityMenuRef.current && !mobileUtilityMenuRef.current.contains(target)) {
        setMobileUtilityMenuOpen(false);
        setMobileUtilityMenuView('root');
      }
    };

    if (notificationsOpen || projectMenuOpen || mobileUtilityMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [mobileUtilityMenuOpen, notificationsOpen, projectMenuOpen]);

  const handleNewProject = useCallback(async (options?: { openChat?: boolean }) => {
    if (isCreating) return;

    setIsCreating(true);
    try {
      const projectName = isMobile
        ? t('newConversationDefaultName', { date: formatDefaultProjectNameDate() })
        : workspaceT('newProjectDefaultName', { date: formatDefaultProjectNameDate() });
      const response = await projectApi.createProject({ name: projectName });
      if (response.error || !response.data) {
        const { toast } = await import('@/lib/stores/toast-store');
        toast.showError(response.error, 'canvas', { fallbackMessage: workspaceT('failedToCreateProject') });
        return;
      }

      const newProject = response.data;
      useProjectStore.getState().addProject({
        id: newProject.id,
        name: newProject.name || projectName,
        updatedAt: new Date().toISOString(),
      });
      setProjectMenuOpen(false);
      // New projects start with their conversation panel open. The outer dobi
      // navigation keeps its independent project-entry collapse policy.
      prepareVisualEditorProjectChatSidebar(newProject.id, options?.openChat ?? true);
      onProjectSelect(newProject.id);
    } catch (error) {
      console.error('创建项目失败:', error);
      const { toast } = await import('@/lib/stores/toast-store');
      toast.showError(error, 'canvas', { fallbackMessage: workspaceT('failedToCreateProject') });
    } finally {
      setIsCreating(false);
    }
  }, [isCreating, isMobile, onProjectSelect, t, workspaceT]);

  const handleProjectShortcutToggle = useCallback(() => {
    setNotificationsOpen(false);
    setMobileUtilityMenuOpen(false);
    setMobileUtilityMenuView('root');
    setProjectMenuOpen((open) => !open);
  }, []);

  const handleProjectShortcutSelect = useCallback((projectId: string) => {
    setProjectMenuOpen(false);
    setNotificationsOpen(false);
    setMobileUtilityMenuOpen(false);
    setMobileUtilityMenuView('root');
    onProjectSelect(projectId);
  }, [onProjectSelect]);

  const handleProjectShortcutDelete = useCallback(async (projectId: string) => {
    const confirmed = await systemDialog.confirm({
      title: workspaceT('confirmDeleteTitle'),
      message: workspaceT('deleteConfirmMessage'),
      confirmLabel: commonT('delete'),
      cancelLabel: commonT('cancel'),
      variant: 'danger',
    });
    if (!confirmed) {
      return false;
    }

    const nextProjectId = projects.find((project) => project.id !== projectId)?.id ?? null;
    try {
      await removeProject(projectId);
      const { toast } = await import('@/lib/stores/toast-store');
      toast.success(workspaceT('projectDeleted'));
      if (projectId === activeProjectId) {
        setProjectMenuOpen(false);
        if (nextProjectId) {
          onProjectSelect(nextProjectId);
        } else {
          onNavChange('explore');
        }
      }
      return true;
    } catch (error) {
      const { toast } = await import('@/lib/stores/toast-store');
      toast.showError(error, 'canvas', { fallbackMessage: workspaceT('deleteFailed') });
      return false;
    }
  }, [activeProjectId, commonT, onNavChange, onProjectSelect, projects, removeProject, workspaceT]);

  const handleNotificationsToggle = useCallback(() => {
    const nextOpen = !notificationsOpen;
    if (nextOpen) {
      void loadInboxMessages({ markRead: true });
    }
    setNotificationsOpen(nextOpen);
  }, [loadInboxMessages, notificationsOpen]);

  const handleInboxClaim = useCallback(async (message: InboxMessage) => {
    if (claimingInboxMessageId) {
      return;
    }
    setClaimingInboxMessageId(message.id);
    setInboxError(null);
    try {
      const response = await inboxApi.claim(message.id);
      if (response.error) throw response.error;
      const claimResult = response.data?.claim_result ?? {};
      setInboxMessages((current) => current.map((item) => (
        item.id === message.id
          ? {
              ...item,
              status: 'claimed',
              claimed_at: item.claimed_at ?? new Date().toISOString(),
              claim_result: claimResult,
            }
          : item
      )));
      if (message.campaign.campaign_type === 'credits') {
        void useCreditsStore.getState().refetchCredits();
      }
      const { toast } = await import('@/lib/stores/toast-store');
      toast.success(
        message.campaign.campaign_type === 'credits'
          ? t('inbox.claimCreditsSuccess')
          : message.campaign.campaign_type === 'coupon'
            ? t('inbox.claimCouponSuccess')
            : t('inbox.taskOpened'),
      );
    } catch (error) {
      setInboxError(error instanceof Error ? error.message : t('inbox.claimFailed'));
      const { toast } = await import('@/lib/stores/toast-store');
      toast.showError(error, 'admin', { fallbackMessage: t('inbox.claimFailed') });
    } finally {
      setClaimingInboxMessageId(null);
    }
  }, [claimingInboxMessageId, t]);

  const handleInboxAction = useCallback((message: InboxMessage) => {
    const actionUrl = message.campaign.action_url;
    if (message.campaign.campaign_type === 'coupon' && message.status === 'claimed') {
      pricingModal.open({ triggerReason: 'manual', quotaType: 'credits', defaultTab: 'topup' });
      setNotificationsOpen(false);
      setMobileUtilityMenuOpen(false);
      setMobileUtilityMenuView('root');
      return;
    }
    if (!actionUrl) {
      return;
    }
    setNotificationsOpen(false);
    setMobileUtilityMenuOpen(false);
    setMobileUtilityMenuView('root');
    if (actionUrl.startsWith('/pricing')) {
      pricingModal.open({ triggerReason: 'manual', quotaType: 'credits', defaultTab: 'topup' });
      return;
    }
    window.location.href = actionUrl;
  }, []);

  const handleExploreViewSelect = useCallback(
    (tab: ExploreTopTab) => {
      if (window.location.pathname + window.location.search !== getExploreHref(tab)) startNavigation(() => onExploreTopTabChange(tab));
    },
    [onExploreTopTabChange, startNavigation],
  );

  const handleRenameProject = useCallback(
    async (event: React.SyntheticEvent | undefined, projectId: string, newName: string) => {
      event?.stopPropagation();
      const trimmed = newName.trim();
      if (!trimmed) return false;

      try {
        const response = await projectApi.updateProject(projectId, { name: trimmed });
        if (response.error || !response.data) {
          const { toast } = await import('@/lib/stores/toast-store');
          toast.showError(response.error, 'canvas', { fallbackMessage: workspaceT('renameFailed') });
          return false;
        }

        useProjectStore.getState().updateProject(projectId, { name: trimmed });
        if (projectId === activeProjectId) {
          await syncVisualEditorProjectName(projectId, trimmed);
        }
        return true;
      } catch (error) {
        console.error('重命名项目失败:', error);
        const { toast } = await import('@/lib/stores/toast-store');
        toast.showError(error, 'canvas', { fallbackMessage: workspaceT('renameFailed') });
        return false;
      }
    },
    [activeProjectId, workspaceT],
  );

  const notificationCenter = (
    <div className="relative" ref={notificationsRef} onKeyDown={(event) => {
      if (event.key === 'Escape' && notificationsOpen) {
        event.preventDefault();
        setNotificationsOpen(false);
        notificationsRef.current?.querySelector('button')?.focus();
      }
    }}>
      <NotificationCenterButton
        label={t('notifications')}
        compact={isProjectView}
        rail={!isMobile}
        open={notificationsOpen}
        highlight={hasUnreadNotifications}
        onClick={handleNotificationsToggle}
      />
      <AnimatePresence>
        {notificationsOpen ? (
          <NotificationsPopover
            side={!isMobile ? 'right' : 'bottom'}
            messages={inboxMessages}
            loading={inboxLoading}
            error={inboxError}
            claimingMessageId={claimingInboxMessageId}
            onRetry={() => void loadInboxMessages({ markRead: true })}
            onClaim={handleInboxClaim}
            onAction={handleInboxAction}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );

  const brandControl = (
    <button
      type="button"
      aria-label={t('home')}
      title={t('home')}
      onClick={() => handleExploreViewSelect('material')}
      className="pointer-events-auto inline-flex shrink-0 items-center gap-2 select-none whitespace-nowrap text-[#111827] transition-opacity duration-150 hover:opacity-72 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/40 focus-visible:ring-offset-2"
    >
      {!isMobile ? <span className={navigationMotion.brandImages} aria-hidden>
        <span data-navigation-brand-icon className="relative block h-8 w-8 shrink-0"><BrandIcon alt="" fill priority sizes="32px" /></span>
        <span data-navigation-brand-wordmark><BrandWordmark alt="" priority className="relative block h-8 w-[140px] shrink-0" sizes="140px" /></span>
      </span> : isProjectView ? (
        <span className="relative block h-7 w-7 shrink-0" aria-hidden><BrandIcon alt="" fill priority sizes="28px" /></span>
      ) : <BrandWordmark alt="" priority className="relative block h-8 w-[140px] shrink-0" sizes="140px" /> }
    </button>
  );

  const projectShortcutControl = (
    <div
      className="relative shrink-0"
      ref={projectMenuRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && projectMenuOpen) {
          event.preventDefault();
          setProjectMenuOpen(false);
          projectMenuRef.current?.querySelector('button')?.focus();
        }
      }}
    >
      {!isMobile ? (
        <RailButton icon={isCreating ? Loader2 : DobiProjectsIcon} label={railT('projects')}
          active={isProjectView || projectMenuOpen} onClick={handleProjectShortcutToggle}
          disabled={isCreating} aria-haspopup="menu" aria-expanded={projectMenuOpen} />
      ) : isProjectView ? (
        <button type="button" onClick={handleProjectShortcutToggle} aria-label={t('myProjects')} title={t('myProjects')}
          aria-haspopup="menu" aria-expanded={projectMenuOpen} disabled={isCreating}
          className="flex h-8 w-7 shrink-0 items-center justify-center rounded-lg text-[#7F8899] hover:bg-[#E9EDF2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4ABDE9] disabled:opacity-40">
          {isCreating ? <Loader2 size={17} className="animate-spin" /> : <DobiProjectsIcon size={20} active={isProjectView || projectMenuOpen} />}
        </button>
      ) : <ProjectShortcutButton icon={DobiProjectsIcon} label={t('projectNav')} ariaLabel={t('myProjects')}
        active={projectMenuOpen} onClick={handleProjectShortcutToggle} disabled={isCreating} loading={isCreating} />}
      <AnimatePresence>
        {projectMenuOpen ? (
          <ProjectShortcutPopover
            anchorRef={projectMenuRef}
            popoverRef={projectPopoverRef}
            side={!isMobile ? 'right' : 'bottom'}
            loading={projectsLoading}
            error={projectsError}
            onRetry={() => void fetchProjects(true)}
            projects={projects}
            categories={projectCategories}
            collaboratedProjects={collaboratedProjects}
            activeProjectId={activeProjectId}
            isCreating={isCreating}
            onNewProject={handleNewProject}
            onProjectSelect={handleProjectShortcutSelect}
            onCreateCategory={createCategory}
            onRenameCategory={updateCategory}
            onDeleteCategory={deleteCategory}
            onMoveProject={moveProjectToCategory}
            onRenameProject={(projectId, newName) => handleRenameProject(undefined, projectId, newName)}
            onDeleteProject={handleProjectShortcutDelete}
            t={t}
            cancelLabel={commonT('cancel')}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );

  const myAssetsActive = !isProjectView
    && (activeNav === 'explore' || activeNav === null)
    && exploreTopTab === 'my-assets';

  const workspaceMetaControls = (
    <>
      <TopbarTextButton
        active={myAssetsActive}
        icon={DobiAssetsIcon}
        label={exploreT('tabMyAssets')}
        onClick={() => handleExploreViewSelect('my-assets')}
      />
      {assistantControl ? (
        <TopbarTextButton
          label={assistantControl.label}
          active={assistantControl.active}
          tone={assistantControl.tone}
          onClick={assistantControl.onClick}
        />
      ) : null}
    </>
  );

  const messageControls = (
    <div className={isProjectView ? 'flex items-center gap-0' : 'contents'}>
      {notificationCenter}
      {isAuthenticated ? (
        <TopbarTextButton
          icon={UsersRound}
          className={isProjectView
            ? 'h-[29px] w-[27px] rounded-md md:h-[31px] md:w-[29px] [&_svg]:stroke-[1.8]'
            : undefined}
          label={t('creatorReferrals')}
          onClick={() => creatorReferralsModal.open('share', { source: 'header' })}
        />
      ) : null}
    </div>
  );

  const accountControls = (
    <div className={cn('pointer-events-auto', isProjectView || isMobile ? 'flex shrink-0 items-center gap-1' : 'contents')}>
      {isAuthenticated && authUserId ? (
        <>
          <LanguageSwitcher currentLocale={locale} label={t('language')} variant="toolbar" />
          <TopbarTextButton
            icon={Tag}
            showLabel
            label={t('pricing')}
            onClick={() => pricingModal.open({ triggerReason: 'manual', defaultTab: 'upgrade' })}
          />
          <UserAvatarMenu
            size={isProjectView ? 26 : isMobile ? 30 : 32}
            showInlineName={false}
            popoverDirection="down"
            popoverAlign="end"
            deferCreditsInit={isCanvasRoute}
          />
        </>
      ) : (
        <StudioAction size="compact" onClick={() => requestLogin(buildLoginRedirectPath())}>{authT('login')}</StudioAction>
      )}
    </div>
  );

  const utilityControls = (
    <div className={isProjectView ? 'pointer-events-auto flex min-w-0 shrink-0 items-center gap-0.5' : 'contents'}>
      <div className={isProjectView ? 'flex items-center gap-0 has-[[data-canvas-resource-actions-slot]:empty]:hidden' : 'contents'}>
        {isProjectView && activeProjectId ? (
          <div
            data-canvas-resource-actions-slot
            className="flex h-9 shrink-0 items-center empty:hidden"
          />
        ) : workspaceMetaControls}
      </div>
      <div className={isProjectView ? 'flex items-center gap-0' : 'contents'}>
        {messageControls}
      </div>
    </div>
  );

  const mobileTopBarStyle = {
    paddingTop: 'env(safe-area-inset-top)',
  } as React.CSSProperties;

  const desktopRail = (
    <>
    <GlobalNavigationRail label={railT('navigation')} brand={brandControl}
      primary={<>
        <RailButton icon={DobiExploreIcon} label={railT('explore')} active={!isProjectView && activeNav === 'explore' && exploreTopTab === 'material' && pathname === '/explore'}
          onPointerEnter={() => prefetchDestination('/explore')} onFocus={() => prefetchDestination('/explore')}
          onClick={() => handleExploreViewSelect('material')} />
        {projectShortcutControl}
        <RailButton icon={DobiAssetsIcon} label={exploreT('tabMyAssets')} active={myAssetsActive}
          onPointerEnter={() => prefetchDestination('/explore?tab=assets')} onFocus={() => prefetchDestination('/explore?tab=assets')}
          onClick={() => handleExploreViewSelect('my-assets')} />
        <div className="mt-2 border-t border-[#E0E6EC] pt-3 [@media(max-height:720px)]:mt-1 [@media(max-height:720px)]:pt-2">
          <p data-navigation-section-label aria-hidden={navigationCollapsed} className="mb-2 px-3 text-[11px] font-medium text-[#7F8899]">{railT('tools')}</p>
          <div className="flex flex-col gap-1" aria-label={railT('tools')}>
            {EXPLORE_QUICK_START_ITEMS.map(({ labelKey, templateId }) => {
              const href = toolsNavigation ? getStudioToolHref(labelKey) : templateId ? `/t/${encodeURIComponent(templateId)}` : '/kagemane';
              const navigatesToRoute = Boolean(toolsNavigation) || labelKey !== 'virtualTryOn';
              const pending = pendingTool === labelKey;
              return <RailButton key={labelKey} icon={pending ? Loader2 : QUICK_START_ICONS[labelKey]}
                aria-busy={pending || undefined}
                linkProps={navigatesToRoute ? {
                  href,
                  onNavigate: () => { if (href !== pathname) setPendingTool(labelKey); },
                  onPointerEnter: () => prefetchTool(href),
                  onFocus: () => prefetchTool(href),
                  'aria-busy': pending || undefined,
                } : undefined}
                component={!toolsNavigation && labelKey === 'virtualTryOn' ? SidebarVirtualTryOnButton : undefined}
                label={exploreT(`quickStart.items.${labelKey}.${labelKey === 'silhouetteExtension' ? 'navigationTitle' : 'title'}`)} data-sidebar-quick-start={labelKey}
                active={toolsNavigation ? toolsNavigation.active === labelKey : labelKey === 'kagemane' ? pathname === '/kagemane' : Boolean(templateId && pathname === `/t/${templateId}`)}
                />;
            })}
            <RailButton icon={Camera} label={exploreT('quickStart.items.cinemaStudio.title')}
              data-sidebar-quick-start="cinemaStudio" active={pathname === '/cinema-studio'}
              onPointerEnter={() => prefetchTool('/cinema-studio')} onFocus={() => prefetchTool('/cinema-studio')}
              onClick={() => openTool('/cinema-studio')} />
          </div>
        </div>
      </>}
      secondary={<>
        <NavigationRailMenu key={`messages-${pathname}-${locale}`} icon={Bell} label={railT('messagesAndContact')}
          highlight={hasUnreadNotifications} onOpen={() => { void loadInboxMessages({ markRead: true }); }}
          footer={close => <div className="space-y-1 border-t border-[#E5E8ED] pt-2">
            <button type="button" className={NAVIGATION_MENU_ITEM_CLASS_NAME}
              onClick={() => { close(); setNotificationsOpen(false); setProjectMenuOpen(false); setEnterpriseOpen(true); }}>
              <Mail className="h-5 w-5 shrink-0" aria-hidden />{railT('contactUs')}
            </button>
            {isAuthenticated && <button type="button" className={NAVIGATION_MENU_ITEM_CLASS_NAME}
              onClick={() => { close(); creatorReferralsModal.open('share', { source: 'header' }); }}>
              <UsersRound className="h-5 w-5 shrink-0" aria-hidden />{t('creatorReferrals')}
            </button>}
          </div>}>
          {close => <NotificationsPopover embedded messages={inboxMessages} loading={inboxLoading}
            error={inboxError} claimingMessageId={claimingInboxMessageId}
            onRetry={() => { void loadInboxMessages({ markRead: true }); }}
            onClaim={handleInboxClaim} onAction={message => { close(); handleInboxAction(message); }} />}
        </NavigationRailMenu>
        <div className="w-full">
          <LanguageSwitcher currentLocale={locale} label={t('language')} variant="sidebar" showLabel placement="right" />
        </div>
        <PricingNavigationButton enabled={!isCanvasRoute || !navigationCollapsed} />
      </>}
      account={isAuthenticated && authUserId ? (
        <UserAvatarMenu size={32} variant="sidebar"
          className="w-full"
          popoverDirection="right" popoverAlign="end" deferCreditsInit={isCanvasRoute} />
      ) : <RailButton icon={UsersRound} label={authT('login')} material="primary" onClick={() => requestLogin(buildLoginRedirectPath())} />}
    />
    {enterpriseOpen && <EnterpriseContactDialog onClose={() => setEnterpriseOpen(false)} />}
    </>
  );

  const mobileUtilityControls = (
    <div className="pointer-events-auto relative flex shrink-0" ref={mobileUtilityMenuRef}>
      <button
        type="button"
        onClick={() => {
          setProjectMenuOpen(false);
          setNotificationsOpen(false);
          setMobileUtilityMenuOpen((open) => {
            if (open) {
              setMobileUtilityMenuView('root');
            }
            return !open;
          });
        }}
        aria-label={welcomePending ? `${t('moreActions')} · ${rewardT('unclaimed')}` : t('moreActions')}
        title={t('moreActions')}
        aria-haspopup="menu"
        aria-expanded={mobileUtilityMenuOpen}
        className={cn(
          'relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#151B23] transition-colors hover:bg-black/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35',
          mobileUtilityMenuOpen && 'bg-black/[0.05]',
        )}
      >
        <MoreHorizontal className="h-[21px] w-[21px]" strokeWidth={2.1} aria-hidden />
        {hasUnreadNotifications && <span aria-hidden className="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#4ABDE9]" />}
      </button>
      <AnimatePresence>
        {mobileUtilityMenuOpen ? (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={delosTransition.micro}
            className={`${navigationStyles.popover} fixed right-3 top-[calc(env(safe-area-inset-top)+60px)] z-[210] w-[min(calc(100vw-24px),340px)] overflow-visible rounded-[14px] border border-[#DCE4EC] bg-white p-2 shadow-[0_24px_70px_rgba(15,23,42,0.20)] ring-1 ring-black/[0.03]`}
            role="menu"
            aria-label={t('moreActions')}
          >
            <LiquidGlass className={navigationStyles.surfaceLens}><span /></LiquidGlass>
            {mobileUtilityMenuView === 'root' ? (
              <div className="space-y-1">
                <MobileUtilityMenuItem
                  icon={DobiAssetsIcon}
                  label={exploreT('tabMyAssets')}
                  active={myAssetsActive}
                  onClick={() => {
                    setMobileUtilityMenuOpen(false);
                    handleExploreViewSelect('my-assets');
                  }}
                />
                <MobileUtilityMenuItem
                  icon={Bell}
                  label={t('notifications')}
                  highlight={hasUnreadNotifications}
                  showChevron
                  onClick={() => {
                    setMobileUtilityMenuView('notifications');
                    void loadInboxMessages({ markRead: true });
                  }}
                />
                {isAuthenticated ? (
                  <MobileUtilityMenuItem
                    icon={UsersRound}
                    label={t('creatorReferrals')}
                    onClick={() => {
                      setMobileUtilityMenuOpen(false);
                      creatorReferralsModal.open('share', { source: 'header' });
                    }}
                  />
                ) : null}
              </div>
            ) : (
              <NotificationsPopover
                embedded
                onBack={() => setMobileUtilityMenuView('root')}
                messages={inboxMessages}
                loading={inboxLoading}
                error={inboxError}
                claimingMessageId={claimingInboxMessageId}
                onRetry={() => void loadInboxMessages({ markRead: true })}
                onClaim={handleInboxClaim}
                onAction={handleInboxAction}
              />
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
  // An opaque header avoids tinted backdrop-filter compositing tiles around
  // the avatar in narrow embedded browser viewports.
  const mobileClaudeHeader = (
    <header
      data-workspace-mobile-header
      className={`${navigationStyles.mobileHeader} z-[120] w-full shrink-0 bg-white px-2`}
      style={mobileTopBarStyle}
    >
      <div className="flex h-14 items-center justify-between gap-1">
        <button
          type="button"
          aria-label={t('home')}
          onClick={() => handleExploreViewSelect('material')}
          className="inline-flex min-w-0 items-center justify-start rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35"
        >
          <BrandWordmark
            alt=""
            priority
            className="relative h-5 w-[92px] shrink-0"
            imageClassName="object-contain object-left"
            sizes="92px"
          />
        </button>
        <div className="flex min-w-0 items-center gap-0">
          {activeNav === 'admin' ? null : (
            <div className="relative flex" ref={projectMenuRef}>
              <button
                type="button"
                onClick={handleProjectShortcutToggle}
                disabled={isCreating}
                aria-label={t('openConversationHistory')}
                title={t('openConversationHistory')}
                aria-haspopup="menu"
                aria-expanded={projectMenuOpen}
                className={cn(
                  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#151B23] transition-colors hover:bg-black/[0.05] disabled:opacity-45',
                  projectMenuOpen && 'bg-black/[0.05]',
                )}
              >
                {isCreating ? (
                  <Loader2 className="h-[19px] w-[19px] animate-spin" strokeWidth={1.9} aria-hidden />
                ) : (
                  <MessageCircle className="h-[19px] w-[19px]" strokeWidth={1.9} aria-hidden />
                )}
              </button>
              <AnimatePresence>
                {projectMenuOpen ? (
                  <MobileConversationProjectMenu
                    projects={projects}
                    loading={projectsLoading}
                    error={projectsError}
                    isCreating={isCreating}
                    onRetry={() => void fetchProjects(true)}
                    onNewProject={() => void handleNewProject({ openChat: true })}
                    onProjectSelect={handleProjectShortcutSelect}
                    t={t}
                  />
                ) : null}
              </AnimatePresence>
            </div>
          )}
          {activeNav === 'admin' ? accountControls : (
            <>
              {mobileUtilityControls}
              {accountControls}
            </>
          )}
        </div>
      </div>
    </header>
  );

  if (isProjectView) {
    return <>
      {!isMobile && desktopRail}
      <WorkspaceNavigation brand={isMobile ? brandControl : null} projects={isMobile ? projectShortcutControl : null} account={accountControls}
        utilities={isMobile ? utilityControls : null} />
    </>;
  }

  if (isMobile) {
    return (
      <>
        {mobileClaudeHeader}
      </>
    );
  }

  return (
    <>
      {desktopRail}

    </>
  );
}

interface ProjectShortcutButtonProps {
  label: string;
  ariaLabel: string;
  icon?: IconComponent;
  active?: boolean;
  truncateLabel?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
}

function ProjectShortcutButton({
  label,
  ariaLabel,
  icon: Icon,
  active = false,
  truncateLabel = false,
  disabled = false,
  loading = false,
  onClick,
}: ProjectShortcutButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={truncateLabel ? label : undefined}
      style={{ fontFamily: 'var(--font-ui)' }}
      className={cn(
        TOPBAR_TEXT_BUTTON_CLASSNAME,
        'gap-1 text-[12px]',
        truncateLabel && 'min-w-0 max-w-full overflow-hidden',
        'disabled:cursor-not-allowed disabled:opacity-50',
        active
          ? 'text-hr-text-primary'
          : 'text-hr-text-secondary hover:text-hr-text-primary',
      )}
    >
      {loading ? (
        <Loader2 className="h-[15px] w-[15px] shrink-0 animate-spin text-hr-text-tertiary" />
      ) : Icon ? (
        <Icon className="h-[15px] w-[15px] shrink-0 text-current opacity-80" strokeWidth={2} aria-hidden />
      ) : null}
      <span className={cn('whitespace-nowrap', truncateLabel && 'min-w-0 flex-1 max-w-[240px] truncate')}>
        {label}
      </span>
      {!loading ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-current opacity-70" aria-hidden /> : null}
    </button>
  );
}

interface MobileConversationProjectMenuProps {
  projects: ProjectRecord[];
  loading: boolean;
  error: string | null;
  isCreating: boolean;
  onRetry: () => void;
  onNewProject: () => void;
  onProjectSelect: (projectId: string) => void;
  t: ReturnType<typeof useTranslations<'nav'>>;
}

function MobileConversationProjectMenu({
  projects,
  loading,
  error,
  isCreating,
  onRetry,
  onNewProject,
  onProjectSelect,
  t,
}: MobileConversationProjectMenuProps) {
  const commonT = useTranslations('common');
  const [visibleCount, setVisibleCount] = useState(PROJECT_MENU_PAGE_SIZE);
  const showMore = visibleCount < projects.length;
  const loadMore = () => setVisibleCount((count) => Math.min(projects.length, count + PROJECT_MENU_PAGE_SIZE));

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={delosTransition.micro}
      className={`${navigationStyles.popover} absolute right-0 top-full z-[180] mt-2 w-[min(92vw,360px)] overflow-hidden border border-hr-border-dim bg-white p-2`}
      role="menu"
      aria-label={t('conversationHistory')}
    >
      <LiquidGlass className={navigationStyles.surfaceLens}><span /></LiquidGlass>
      <div className="flex items-center justify-between gap-3 px-2 pb-2 pt-1">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-[#182230]">{t('conversationHistory')}</p>
        </div>
        <StudioAction
          size="compact"
          type="button"
          role="menuitem"
          onClick={onNewProject}
          disabled={isCreating}
          className="shrink-0 gap-2 text-[13px]"
        >
          {isCreating ? (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} aria-hidden />
          ) : (
            <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
          )}
          {t('newConversation')}
        </StudioAction>
      </div>

      <div className="max-h-[min(54vh,420px)] overflow-y-auto overscroll-contain pr-0.5 scrollbar-thin"
        onScroll={(event) => {
          const target = event.currentTarget;
          if (showMore && target.scrollTop + target.clientHeight >= target.scrollHeight - 120) loadMore();
        }}>
        {loading && projects.length === 0 ? (
          <div className="flex items-center justify-center px-3 py-8 text-hr-text-tertiary">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          </div>
        ) : error && projects.length === 0 ? (
          <div className="space-y-2 px-3 py-6 text-center">
            <p className="text-xs text-hr-text-tertiary">{t('failedToLoadProjects')}</p>
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-8 items-center justify-center rounded-[10px] bg-surface-overlay px-3 text-xs font-semibold text-[#0F769D] transition-colors hover:bg-[#EAF8FD]"
            >
              {t('retry')}
            </button>
          </div>
        ) : projects.length > 0 ? (
          <div className="space-y-0.5">
            {projects.slice(0, visibleCount).map((project) => (
              <button
                key={project.id}
                type="button"
                role="menuitem"
                onClick={() => onProjectSelect(project.id)}
                className="flex min-h-[54px] w-full items-center gap-3 rounded-[8px] px-2.5 py-2 text-left text-[#344054] transition-colors hover:bg-[#F5F7F9] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35"
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-[11px] bg-[#F0F3F6]">
                  {project.thumbnailUrl ? (
                    <SidebarThumbnailImage
                      sourceUrl={project.thumbnailUrl}
                      targetWidth={120}
                      quality={90}
                      displaySize={80}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#667085]">
                      <MessageCircle className="h-[18px] w-[18px]" strokeWidth={1.9} aria-hidden />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-[#182230]">{project.name}</p>
                  <p className="mt-0.5 truncate text-[11px] text-[#7A8494]">
                    {t('conversationProjectItems', { count: project.nodeCount ?? 0 })}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-[#98A2B3]" strokeWidth={1.9} aria-hidden />
              </button>
            ))}
            {showMore ? <button type="button" role="menuitem" onClick={loadMore}
              className="w-full rounded-[8px] px-3 py-2 text-center text-xs font-semibold text-[#0F769D] hover:bg-[#EAF8FD] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/40">
              {commonT('loadMore')}
            </button> : null}
          </div>
        ) : (
          <div className="px-3 py-8 text-center text-xs text-hr-text-tertiary">
            {t('noConversationHistory')}
          </div>
        )}
      </div>
    </motion.div>
  );
}

interface ProjectShortcutPopoverProps {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  popoverRef: React.RefObject<HTMLDivElement | null>;
  side?: 'right' | 'bottom';
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  projects: ProjectRecord[];
  categories: ProjectCategoryRecord[];
  collaboratedProjects: ProjectRecord[];
  activeProjectId?: string | null;
  isCreating: boolean;
  onNewProject: () => void;
  onProjectSelect: (projectId: string) => void;
  onCreateCategory: (name: string) => Promise<ProjectCategoryRecord | null>;
  onRenameCategory: (id: string, updates: { name?: string; sortOrder?: number }) => Promise<boolean>;
  onDeleteCategory: (id: string) => Promise<boolean>;
  onMoveProject: (projectId: string, categoryId: string | null) => Promise<boolean>;
  onRenameProject: (projectId: string, newName: string) => Promise<boolean>;
  onDeleteProject: (projectId: string) => Promise<boolean>;
  t: ReturnType<typeof useTranslations<'nav'>>;
  cancelLabel: string;
}

function ProjectShortcutPopover({
  anchorRef,
  popoverRef,
  side = 'bottom',
  loading,
  error,
  onRetry,
  projects,
  categories,
  collaboratedProjects,
  activeProjectId,
  isCreating,
  onNewProject,
  onProjectSelect,
  onCreateCategory,
  onRenameCategory,
  onDeleteCategory,
  onMoveProject,
  onRenameProject,
  onDeleteProject,
  t,
  cancelLabel,
}: ProjectShortcutPopoverProps) {
  const { isMobile } = useResponsiveLayout();
  const commonT = useTranslations('common');
  const portalled = !isMobile && side === 'right';
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!portalled) return;
    // The short-window navigation rail scrolls and clips its descendants.
    // Anchor a body portal to the trigger so the menu remains visible there.
    const place = () => {
      const anchor = anchorRef.current;
      const panel = popoverRef.current;
      if (!anchor || !panel) return;
      const bounds = anchor.getBoundingClientRect();
      setPosition({
        left: Math.max(12, Math.min(bounds.right + 12, window.innerWidth - panel.offsetWidth - 12)),
        top: Math.max(12, Math.min(bounds.top, window.innerHeight - panel.offsetHeight - 12)),
      });
    };
    place();
    const observer = new ResizeObserver(place);
    if (popoverRef.current) observer.observe(popoverRef.current);
    if (anchorRef.current) observer.observe(anchorRef.current);
    const onScroll = (event: Event) => {
      if (event.target === window || !popoverRef.current?.contains(event.target as Node)) place();
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [anchorRef, popoverRef, portalled]);

  const [visibleCount, setVisibleCount] = useState(PROJECT_MENU_PAGE_SIZE);
  const [collapsedCategoryIds, setCollapsedCategoryIds] = useState<string[]>([]);
  const [categoryMenuId, setCategoryMenuId] = useState<string | null>(null);
  const [moveMenuProjectId, setMoveMenuProjectId] = useState<string | null>(null);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingProjectName, setEditingProjectName] = useState('');
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  type ProjectGroup = {
    id: string | null;
    name: string;
    projects: ProjectRecord[];
    canEdit: boolean;
  };

  const categoryById = useMemo(() => new Map(categories.map((category) => [category.id, category])), [categories]);
  const projectGroups = useMemo<ProjectGroup[]>(() => {
    const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
    const knownCategoryIds = new Set(sortedCategories.map((category) => category.id));
    const groups: ProjectGroup[] = sortedCategories.map((category) => ({
      id: category.id,
      name: category.name,
      projects: projects.filter((project) => project.categoryId === category.id),
      canEdit: true,
    }));
    const uncategorizedProjects = projects.filter((project) => (
      !project.categoryId || !knownCategoryIds.has(project.categoryId)
    ));

    if (uncategorizedProjects.length > 0 || (categories.length === 0 && projects.length > 0)) {
      groups.push({
        id: null,
        name: t('uncategorizedProjects'),
        projects: uncategorizedProjects,
        canEdit: false,
      });
    }

    return groups;
  }, [categories, projects, t]);
  const visibleGroups = useMemo(() => {
    let remaining = visibleCount;
    return projectGroups.flatMap((group) => {
      if (group.projects.length === 0 || collapsedCategoryIds.includes(group.id ?? 'uncategorized')) {
        return [{ ...group, projects: [], totalCount: group.projects.length }];
      }
      if (remaining <= 0) return [];
      const visibleProjects = group.projects.slice(0, remaining);
      remaining -= visibleProjects.length;
      return [{ ...group, projects: visibleProjects, totalCount: group.projects.length }];
    });
  }, [collapsedCategoryIds, projectGroups, visibleCount]);
  const expandedProjectCount = projectGroups.reduce((count, group) => (
    count + (collapsedCategoryIds.includes(group.id ?? 'uncategorized') ? 0 : group.projects.length)
  ), 0);
  const visibleCollaboratedProjects = collaboratedProjects.slice(0, Math.max(0, visibleCount - expandedProjectCount));
  const showMore = visibleCount < expandedProjectCount + collaboratedProjects.length;
  const loadMore = () => setVisibleCount((count) => Math.min(
    expandedProjectCount + collaboratedProjects.length, count + PROJECT_MENU_PAGE_SIZE,
  ));

  const toggleCategoryCollapsed = (categoryId: string) => {
    setCollapsedCategoryIds((current) => (
      current.includes(categoryId)
        ? current.filter((id) => id !== categoryId)
        : [...current, categoryId]
    ));
  };

  const submitCreateCategory = async () => {
    const category = await onCreateCategory(categoryDraft);
    if (!category) return;
    setCategoryDraft('');
    setCreatingCategory(false);
    setCollapsedCategoryIds((current) => current.filter((id) => id !== category.id));
  };

  const submitCategoryRename = async (categoryId: string) => {
    const renamed = await onRenameCategory(categoryId, { name: editingCategoryName });
    if (!renamed) return;
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };

  const handleMoveProject = async (project: ProjectRecord, categoryId: string | null) => {
    setMoveMenuProjectId(null);
    await onMoveProject(project.id, categoryId);
  };

  const startProjectRename = (project: ProjectRecord) => {
    setMoveMenuProjectId(null);
    setEditingProjectId(project.id);
    setEditingProjectName(project.name);
  };

  const cancelProjectRename = () => {
    setEditingProjectId(null);
    setEditingProjectName('');
  };

  const submitProjectRename = async (project: ProjectRecord) => {
    if (renamingProjectId) return;
    const trimmed = editingProjectName.trim();
    if (!trimmed) return;

    if (trimmed === project.name.trim()) {
      cancelProjectRename();
      return;
    }

    setRenamingProjectId(project.id);
    try {
      const renamed = await onRenameProject(project.id, trimmed);
      if (renamed) {
        cancelProjectRename();
      }
    } finally {
      setRenamingProjectId(null);
    }
  };

  const handleDeleteProject = async (project: ProjectRecord) => {
    if (deletingProjectId) return;
    setMoveMenuProjectId(null);
    setDeletingProjectId(project.id);
    try {
      await onDeleteProject(project.id);
    } finally {
      setDeletingProjectId(null);
    }
  };

  const renderProjectRow = (project: ProjectRecord, icon: 'project' | 'shared' = 'project') => {
    const active = project.id === activeProjectId;
    const isEditing = editingProjectId === project.id;
    return (
      <div key={project.id} className="group/project">
        <div className={navigationStyles.projectRow}>
          <div
            role={isEditing ? undefined : 'menuitem'}
            tabIndex={isEditing ? undefined : 0}
            onClick={isEditing ? undefined : () => onProjectSelect(project.id)}
            onKeyDown={isEditing ? undefined : (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              onProjectSelect(project.id);
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-[12px] px-2 py-1.5 text-left transition-colors',
              icon === 'project' && !isEditing ? 'pr-24' : 'pr-2',
              active
                ? 'bg-accent-soft text-hr-text-primary'
                : 'text-hr-text-secondary hover:bg-surface-hover hover:text-hr-text-primary',
            )}
          >
            <div className="h-8 w-8 shrink-0 overflow-hidden rounded-[9px] bg-surface-overlay">
              {project.thumbnailUrl ? (
                <SidebarThumbnailImage
                  sourceUrl={project.thumbnailUrl}
                  targetWidth={96}
                  quality={70}
                  displaySize={32}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-hr-text-tertiary">
                  {icon === 'shared' ? <MessageCircle className="h-4 w-4" strokeWidth={1.9} aria-hidden /> : <Home className="h-4 w-4" strokeWidth={1.9} aria-hidden />}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              {isEditing ? (
                <div className="flex min-w-0 items-center gap-1">
                  <input
                    value={editingProjectName}
                    onChange={(event) => setEditingProjectName(event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                      event.stopPropagation();
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void submitProjectRename(project);
                      } else if (event.key === 'Escape') {
                        event.preventDefault();
                        cancelProjectRename();
                      }
                    }}
                    onBlur={() => {
                      if (renamingProjectId !== project.id) {
                        cancelProjectRename();
                      }
                    }}
                    disabled={renamingProjectId === project.id}
                    className="h-7 min-w-0 flex-1 rounded-[9px] border border-accent-core/40 bg-white px-2 text-[12.5px] font-semibold text-hr-text-primary outline-none focus:ring-2 focus:ring-accent-soft disabled:opacity-60"
                    autoFocus
                  />
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      void submitProjectRename(project);
                    }}
                    disabled={renamingProjectId === project.id || !editingProjectName.trim()}
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-hr-text-tertiary transition-colors hover:bg-white hover:text-accent-core disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={t('renameConfirm')}
                    title={t('renameConfirm')}
                  >
                    {renamingProjectId === project.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      cancelProjectRename();
                    }}
                    disabled={renamingProjectId === project.id}
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-hr-text-tertiary transition-colors hover:bg-white hover:text-hr-text-primary disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={cancelLabel}
                    title={cancelLabel}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex min-w-0 items-center gap-1.5">
                    <p className="truncate text-[12.5px] font-semibold">{project.name}</p>
                    {active ? (
                      <span className="shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-accent-core">
                        {t('currentWorkspace')}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[10.5px] text-hr-text-tertiary">
                    {t('workspaceItems', { count: project.nodeCount ?? 0 })}
                  </p>
                </>
              )}
            </div>
          </div>
          {icon === 'project' && !isEditing ? (
            <>
              <button
                data-project-action="rename"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  startProjectRename(project);
                }}
                className="absolute right-14 top-1/2 -translate-y-1/2 rounded-md bg-white/90 p-1 text-hr-text-tertiary opacity-0 shadow-sm transition-opacity hover:text-accent-core focus:opacity-100 group-hover/project:opacity-100"
                aria-label={t('rename')}
                title={t('rename')}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                data-project-action="delete"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  void handleDeleteProject(project);
                }}
                disabled={deletingProjectId === project.id}
                className="absolute right-8 top-1/2 -translate-y-1/2 rounded-md bg-white/90 p-1 text-hr-text-tertiary opacity-0 shadow-sm transition-opacity hover:text-semantic-error focus:opacity-100 disabled:cursor-wait disabled:opacity-70 group-hover/project:opacity-100"
                aria-label={t('deleteProject')}
                title={t('deleteProject')}
              >
                {deletingProjectId === project.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                data-project-action="move"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setMoveMenuProjectId((current) => current === project.id ? null : project.id);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-white/90 p-1 text-hr-text-tertiary opacity-0 shadow-sm transition-opacity hover:text-accent-core focus:opacity-100 group-hover/project:opacity-100"
                aria-label={t('moveToCategory')}
                title={t('moveToCategory')}
                aria-haspopup="menu"
                aria-expanded={moveMenuProjectId === project.id}
              >
                <Folder className="h-3.5 w-3.5" />
              </button>
            </>
          ) : null}
        </div>
        {icon === 'project' && moveMenuProjectId === project.id ? (
          <div
            role="menu"
            className="ml-10 mr-2 mt-1 grid grid-cols-1 gap-0.5 rounded-[12px] border border-hr-border-dim bg-surface-base p-1"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => handleMoveProject(project, null)}
              className={cn(
                'flex w-full items-center gap-2 rounded-[9px] px-2 py-1.5 text-left text-[11px] font-medium',
                !project.categoryId ? 'bg-white text-accent-core shadow-sm' : 'text-hr-text-secondary hover:bg-white hover:text-hr-text-primary',
              )}
            >
              <Folder className="h-3.5 w-3.5" />
              {t('uncategorizedProjects')}
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                role="menuitem"
                onClick={() => handleMoveProject(project, category.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-[9px] px-2 py-1.5 text-left text-[11px] font-medium',
                  project.categoryId === category.id ? 'bg-white text-accent-core shadow-sm' : 'text-hr-text-secondary hover:bg-white hover:text-hr-text-primary',
                )}
              >
                <Folder className="h-3.5 w-3.5" />
                <span className="truncate">{category.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const popover = (
    <motion.div
      ref={popoverRef}
      style={portalled ? { position: 'fixed', left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? 'visible' : 'hidden' } : undefined}
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={delosTransition.micro}
      className={cn(navigationStyles.popover, 'z-[180] overflow-hidden p-2',
        isMobile ? 'fixed inset-x-3 top-[58px] max-h-[calc(100dvh-80px)] overflow-y-auto' : portalled ? 'w-[min(330px,calc(100vw-24px))] max-h-[calc(100dvh-24px)] overflow-y-auto' : 'absolute left-0 top-full mt-2 w-[330px]') }
      role="menu"
    >
      <LiquidGlass className={navigationStyles.surfaceLens}><span /></LiquidGlass>
      <div className="flex items-center justify-between px-2 pb-1.5 pt-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-hr-text-tertiary">
          {t('recentWorkspaces')}
        </p>
      </div>

      <div className="max-h-[360px] overflow-y-auto pr-0.5 scrollbar-thin"
        onScroll={(event) => {
          const target = event.currentTarget;
          if (showMore && target.scrollTop + target.clientHeight >= target.scrollHeight - 120) loadMore();
        }}>
        {loading && projects.length === 0 ? (
          <div role="status" aria-label={commonT('loading')} className="flex justify-center px-3 py-8"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /></div>
        ) : error && projects.length === 0 ? (
          <div className="space-y-2 px-3 py-6 text-center">
            <p role="alert" className="text-xs text-hr-text-tertiary">{t('failedToLoadProjects')}</p>
            <button type="button" onClick={onRetry} className="rounded-lg px-3 py-2 text-xs font-semibold text-[#137EA8] hover:bg-[#4ABDE9]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4ABDE9]">{t('retry')}</button>
          </div>
        ) : projectGroups.length > 0 ? (
          <div className="space-y-0.5">
            {visibleGroups.map((group) => {
              const groupKey = group.id ?? 'uncategorized';
              const isCollapsed = collapsedCategoryIds.includes(groupKey);
              const category = group.id ? categoryById.get(group.id) : null;

              return (
                <div key={groupKey}>
                  <div className="group/category flex items-center gap-1 px-2 pb-0.5 pt-1.5">
                    {editingCategoryId === group.id && category ? (
                      <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-hr-text-tertiary">
                        <ChevronDown className="h-3 w-3" />
                        <Folder className="h-3 w-3" />
                        <input
                          value={editingCategoryName}
                          onChange={(event) => setEditingCategoryName(event.target.value)}
                          onKeyDown={(event) => {
                            event.stopPropagation();
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              void submitCategoryRename(category.id);
                            } else if (event.key === 'Escape') {
                              event.preventDefault();
                              setEditingCategoryId(null);
                            }
                          }}
                          onBlur={() => setEditingCategoryId(null)}
                          className="min-w-0 flex-1 rounded border border-accent-core/40 bg-white px-1 py-0.5 text-[11px] normal-case tracking-normal text-hr-text-primary outline-none focus:ring-1 focus:ring-accent-soft"
                          autoFocus
                        />
                        <span className="shrink-0 rounded-full bg-surface-overlay px-1.5 py-0.5 text-[9px] tracking-normal">
                          {group.totalCount}
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleCategoryCollapsed(groupKey)}
                        className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-[10px] font-medium uppercase tracking-wider text-hr-text-tertiary transition-colors hover:text-hr-text-primary"
                        aria-expanded={!isCollapsed}
                      >
                        {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        <Folder className="h-3 w-3" />
                        <span className="truncate">{group.name}</span>
                        <span className="shrink-0 rounded-full bg-surface-overlay px-1.5 py-0.5 text-[9px] tracking-normal">
                          {group.totalCount}
                        </span>
                      </button>
                    )}
                    {group.canEdit && category ? (
                      <div className="relative shrink-0">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setCategoryMenuId((current) => current === category.id ? null : category.id);
                          }}
                          className="rounded-md p-1 text-hr-text-tertiary opacity-0 transition-opacity hover:text-hr-text-primary group-hover/category:opacity-100"
                          aria-label={t('categoryActions')}
                          aria-haspopup="menu"
                          aria-expanded={categoryMenuId === category.id}
                        >
                          <MoreHorizontal className="h-3 w-3" />
                        </button>
                        {categoryMenuId === category.id ? (
                          <div
                            role="menu"
                            className="absolute right-0 top-full z-30 mt-1 min-w-[8.5rem] overflow-hidden rounded-xl border border-hr-border-dim bg-white py-1 shadow-[0_14px_32px_rgba(15,23,42,0.14)]"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setCategoryMenuId(null);
                                setEditingCategoryId(category.id);
                                setEditingCategoryName(category.name);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-hr-text-secondary hover:bg-surface-hover hover:text-hr-text-primary"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              {t('renameCategory')}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setCategoryMenuId(null);
                                void onDeleteCategory(category.id);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-semantic-error hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {t('deleteCategory')}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {!isCollapsed ? (
                    <div className="space-y-0.5">
                      {group.projects.length > 0 ? group.projects.map((project) => renderProjectRow(project)) : (
                        <p className="px-3 py-2 text-[11px] text-hr-text-tertiary">{t('emptyCategory')}</p>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-3 py-6 text-center text-xs text-hr-text-tertiary">
            {t('noRecentProjects')}
          </div>
        )}
        {visibleCollaboratedProjects.length > 0 ? (
          <div className="mt-1.5 space-y-0.5 border-t border-hr-border-dim pt-1.5">
            <p className="px-2 pb-0.5 text-[10px] font-medium uppercase tracking-wider text-hr-text-tertiary">
              {t('collaboratedProjects')}
            </p>
            {visibleCollaboratedProjects.map((project) => renderProjectRow(project, 'shared'))}
          </div>
        ) : null}
        {showMore ? <button type="button" role="menuitem" onClick={loadMore}
          className="mt-1 w-full rounded-[9px] px-3 py-2 text-center text-xs font-semibold text-accent-core hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-core/40">
          {commonT('loadMore')}
        </button> : null}
      </div>

      <div className="mt-1.5 space-y-0.5 border-t border-hr-border-dim pt-1.5">
        {creatingCategory ? (
          <div className="flex items-center gap-1.5 rounded-[12px] bg-surface-base px-2 py-1.5">
            <FolderPlus className="h-4 w-4 shrink-0 text-hr-text-tertiary" />
            <input
              value={categoryDraft}
              onChange={(event) => setCategoryDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void submitCreateCategory();
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  setCreatingCategory(false);
                  setCategoryDraft('');
                }
              }}
              placeholder={t('newCategoryPlaceholder')}
              className="min-w-0 flex-1 bg-transparent text-[13px] font-semibold text-hr-text-primary outline-none placeholder:text-hr-text-tertiary"
              autoFocus
            />
            <button
              type="button"
              onClick={() => void submitCreateCategory()}
              className="rounded-md p-1 text-hr-text-tertiary hover:text-accent-core"
              aria-label={t('createCategory')}
            >
              <Check className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setCreatingCategory(true);
              setCategoryDraft('');
            }}
            className="flex w-full items-center gap-2 rounded-[12px] px-2 py-1.5 text-left text-[13px] font-semibold text-hr-text-secondary transition-colors hover:bg-surface-hover hover:text-hr-text-primary"
          >
            <FolderPlus className="h-4 w-4" aria-hidden />
            {t('newCategory')}
          </button>
        )}
        <button
          type="button"
          role="menuitem"
          onClick={onNewProject}
          disabled={isCreating}
          className="flex w-full items-center gap-2 rounded-[12px] px-2 py-1.5 text-left text-[13px] font-semibold text-hr-text-secondary transition-colors hover:bg-surface-hover hover:text-hr-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isCreating ? (
            <Loader2 className="h-4 w-4 animate-spin text-hr-text-tertiary" aria-hidden />
          ) : (
            <Plus className="h-4 w-4" aria-hidden />
          )}
          <span>{t('newWorkspace')}</span>
        </button>
      </div>
    </motion.div>
  );
  return portalled ? createPortal(popover, document.body) : popover;
}

interface NotificationCenterButtonProps {
  label: string;
  compact?: boolean;
  rail?: boolean;
  open?: boolean;
  highlight?: boolean;
  onClick: () => void;
}

interface MobileUtilityMenuItemProps {
  icon: IconComponent;
  label: string;
  active?: boolean;
  highlight?: boolean;
  showChevron?: boolean;
  onClick: () => void;
}

function MobileUtilityMenuItem({
  icon: Icon,
  label,
  active = false,
  highlight = false,
  showChevron = false,
  onClick,
}: MobileUtilityMenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex min-h-12 w-full items-center gap-3 rounded-[11px] px-3 text-left text-[14px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35',
        active ? 'bg-[#EAF8FD] text-[#0F769D]' : 'text-[#263548] hover:bg-[#F5F7F9]',
      )}
    >
      <span className={cn(
        'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]',
        active ? 'bg-white text-[#0F8ABA]' : 'bg-[#F4F6F8] text-[#627386]',
      )}>
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.9} aria-hidden />
        {highlight ? (
          <span className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full border-2 border-white bg-[#4ABDE9]" aria-hidden />
        ) : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {showChevron ? <ChevronRight className="h-4 w-4 shrink-0 text-[#98A2B3]" aria-hidden /> : null}
    </button>
  );
}

function NotificationCenterButton({
  label,
  compact = false,
  rail = false,
  open = false,
  highlight = false,
  onClick,
}: NotificationCenterButtonProps) {
  if (rail) return <RailButton icon={Bell} label={label} highlight={highlight} onClick={onClick} aria-expanded={open} aria-haspopup="dialog" />;
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          TOPBAR_ICON_BUTTON_CLASSNAME,
          getTopbarToneClassName({ active: open }),
          getTopbarIconSurfaceClassName({ active: open }),
          compact && 'h-[29px] w-[27px] rounded-md md:h-[31px] md:w-[29px]',
        )}
      >
        <span className={cn('relative flex items-center justify-center', TOPBAR_ICON_CLASSNAME)}>
          <Bell className={TOPBAR_ICON_CLASSNAME} strokeWidth={compact ? 1.8 : 2.05} aria-hidden />
          {highlight ? (
            <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#4ABDE9]" />
          ) : null}
        </span>
      </button>

      {!open ? (
        <div
          className={cn(
            TOPBAR_ICON_TOOLTIP_CLASSNAME,
            'pointer-events-none',
          )}
        >
          {label}
        </div>
      ) : null}
    </div>
  );
}

interface NotificationsPopoverProps {
  side?: 'right' | 'bottom';
  messages: InboxMessage[];
  loading: boolean;
  error: string | null;
  claimingMessageId: string | null;
  onRetry: () => void;
  onClaim: (message: InboxMessage) => void;
  onAction: (message: InboxMessage) => void;
  embedded?: boolean;
  onBack?: () => void;
}

function NotificationsPopover({
  side = 'bottom',
  messages,
  loading,
  error,
  claimingMessageId,
  onRetry,
  onClaim,
  onAction,
  embedded = false,
  onBack,
}: NotificationsPopoverProps) {
  const t = useTranslations('nav');
  const welcomeStatus = useCreditsStore((state) => state.credits?.welcome_credit_status);
  const hasWelcomeMessage = welcomeStatus === 'pending' || welcomeStatus === 'granted';
  const walletLoading = useCreditsStore((state) => !state.credits && state.loading);
  const walletError = useCreditsStore((state) => !state.credits && state.error);
  const displayError = error || (walletError ? t('inbox.loadFailed') : null);
  const isLoading = loading || walletLoading;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={delosTransition.micro}
      role={embedded ? undefined : "dialog"}
      aria-label={t('inbox.title')}
      className={cn(
        embedded
          ? 'w-full overflow-hidden bg-transparent'
          : cn(navigationStyles.popover, 'absolute z-[220] w-[min(92vw,380px)] overflow-hidden p-2', side === 'right' ? 'left-[calc(var(--global-navigation-width,64px)-12px)] bottom-0 ml-3 max-h-[calc(100dvh-32px)] overflow-y-auto' : 'right-0 top-full mt-2'),
      )}
    >
      {!embedded && <LiquidGlass className={navigationStyles.surfaceLens}><span /></LiquidGlass>}
      <div className="flex items-center justify-between border-b border-[#EEF2F6] px-2.5 pb-2.5 pt-2">
        <div className="flex items-center gap-2">
          {embedded && onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label={t('backToMoreActions')}
              className="-ml-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-[#627386] transition-colors hover:bg-[#F3F5F7] hover:text-[#182230] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2.1} aria-hidden />
            </button>
          ) : (
            <Inbox className="h-4 w-4 text-accent-core" />
          )}
          <span className="text-sm font-bold text-hr-text-primary">{t('inbox.title')}</span>
        </div>
        <button
          type="button"
          onClick={onRetry}
          disabled={isLoading}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-hr-text-tertiary transition hover:bg-surface-muted hover:text-hr-text-primary disabled:opacity-50"
          aria-label={t('inbox.refresh')}
        >
          <SystemLoadingSwitch icon={RefreshCw} className={cn('h-4 w-4', isLoading && 'animate-spin')} />
        </button>
      </div>

      <div className="max-h-[min(72vh,520px)] space-y-2 overflow-y-auto pt-2 pr-0.5 delos-scrollbar">
        <WelcomeRewardMessage />
        {isLoading && messages.length === 0 && !hasWelcomeMessage ? (
          <div className="rounded-[10px] border border-hr-border-dim bg-white px-4 py-8 text-center text-sm text-hr-text-secondary">
            {t('inbox.loading')}
          </div>
        ) : null}
        {displayError ? (
          <div className="rounded-[10px] border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {displayError}
          </div>
        ) : null}
        {!isLoading && messages.length === 0 && !displayError && !hasWelcomeMessage ? (
          <div className="rounded-[10px] border border-hr-border-dim bg-white px-4 py-8 text-center">
            <Inbox className="mx-auto h-7 w-7 text-hr-text-tertiary" />
            <div className="mt-2 text-sm font-semibold text-hr-text-primary">{t('inbox.emptyTitle')}</div>
            <div className="mt-1 text-xs leading-5 text-hr-text-secondary">{t('inbox.emptyDescription')}</div>
          </div>
        ) : null}
        {messages.map((message) => (
          <InboxMessageCard
            key={message.id}
            message={message}
            claiming={claimingMessageId === message.id}
            onClaim={() => onClaim(message)}
            onAction={() => onAction(message)}
          />
        ))}
      </div>
    </motion.div>
  );
}

function getInboxRewardChip(message: InboxMessage, t: ReturnType<typeof useTranslations<'nav'>>) {
  const config = message.campaign.reward_config || {};
  if (isInboxNoticeOnly(message)) {
    return null;
  }
  if (message.campaign.campaign_type === 'coupon') {
    const amount = Number(config.discount_yuan ?? message.claim_result.discount_yuan ?? 0);
    return amount > 0 ? t('inbox.couponChip', { amount }) : t('inbox.couponChipFallback');
  }
  if (message.campaign.campaign_type === 'credits') {
    const credits = Number(config.credits ?? message.claim_result.credits ?? 0);
    return credits > 0 ? t('inbox.creditsChip', { credits }) : t('inbox.creditsChipFallback');
  }
  return null;
}

function isInboxNoticeOnly(message: InboxMessage) {
  const config = message.campaign.reward_config || {};
  return config.notice_only === true || config.notice_type === 'campaign_announcement';
}

interface InboxMessageCardProps {
  message: InboxMessage;
  claiming: boolean;
  onClaim: () => void;
  onAction: () => void;
}

function InboxMessageCard({
  message,
  claiming,
  onClaim,
  onAction,
}: InboxMessageCardProps) {
  const t = useTranslations('nav');
  const [expanded, setExpanded] = useState(false);
  const isUnread = message.status === 'unread';
  const isNoticeOnly = isInboxNoticeOnly(message);
  const isClaimed = !isNoticeOnly && message.status === 'claimed';
  const isExpired = message.status === 'expired';
  const isTask = message.campaign.campaign_type === 'task';
  const canExpand = Boolean(message.campaign.body || message.campaign.ends_at || message.campaign.action_url);
  const rewardChip = getInboxRewardChip(message, t);
  const primaryLabel = isExpired
    ? t('inbox.expired')
    : isClaimed
      ? (message.campaign.campaign_type === 'coupon' ? t('inbox.useNow') : t('inbox.claimed'))
      : isTask
        ? (message.campaign.action_label || t('inbox.view'))
        : t('inbox.claim');

  const handlePrimaryClick = () => {
    if (isExpired || claiming) {
      return;
    }
    if (isClaimed) {
      onAction();
      return;
    }
    if (isNoticeOnly) {
      onAction();
      return;
    }
    if (isTask) {
      onClaim();
      onAction();
      return;
    }
    onClaim();
  };
  const handleExpandToggle = () => {
    setExpanded((current) => !current);
  };

  return (
    <div
      className={cn(
        'flex w-full items-start gap-2.5 rounded-[10px] border px-3 py-2.5 text-left shadow-[0_8px_22px_rgba(15,23,42,0.05)] transition-colors',
        isUnread ? 'border-[#BDECFB] bg-[#F2FBFE]' : 'border-[#E2E8F0] bg-white',
      )}
    >
      <span
        className={cn(
          'mt-2 h-2 w-2 shrink-0 rounded-full transition-colors',
          isUnread ? 'bg-accent-core' : 'bg-transparent',
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={handleExpandToggle}
            disabled={!canExpand}
            aria-expanded={expanded}
            aria-label={expanded ? t('inbox.collapse') : t('inbox.expand')}
            className={cn(
              'inline-flex h-5 w-5 shrink-0 items-center justify-center text-[#8A96A8] transition-colors hover:text-[#5E6B7C] disabled:cursor-default disabled:opacity-40',
            )}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" strokeWidth={2.2} />
            ) : (
              <ChevronRight className="h-4 w-4" strokeWidth={2.2} />
            )}
          </button>
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-5 text-hr-text-primary">
            {message.campaign.title}
          </span>
          {rewardChip ? (
            <span
              className={cn(
                'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                isClaimed
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : isExpired
                    ? 'border-gray-200 bg-gray-50 text-hr-text-tertiary'
                    : 'border-accent-core/15 bg-accent-soft/40 text-accent-core',
              )}
            >
              {rewardChip}
            </span>
          ) : null}
          {isClaimed ? (
            <span className="inline-flex shrink-0 items-center rounded-full border border-accent-core/15 bg-accent-soft/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent-core">
              {t('inbox.claimed')}
            </span>
          ) : null}
        </div>
        {message.campaign.body ? (
          <p
            className={cn(
              'mt-1 text-[12px] leading-5 text-hr-text-secondary',
              expanded ? '' : 'overflow-hidden',
            )}
            style={expanded ? undefined : {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
            }}
          >
            {message.campaign.body}
          </p>
        ) : null}
        {expanded ? (
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-[11px] text-hr-text-tertiary">
              {message.campaign.ends_at ? t('inbox.endsAt', { date: new Date(message.campaign.ends_at).toLocaleDateString() }) : t('inbox.noExpiry')}
            </span>
            <StudioAction
              type="button"
              size="compact"
              variant={isClaimed ? 'secondary' : 'primary'}
              onClick={handlePrimaryClick}
              disabled={isExpired || claiming}
              aria-busy={claiming}
              className="min-h-11 shrink-0 gap-1.5 px-3 text-xs"
            >
              {claiming ? <SystemLoadingSwitch icon={RefreshCw} className="h-3.5 w-3.5 animate-spin" /> : null}
              {primaryLabel}
            </StudioAction>
          </div>
        ) : null}
      </div>
    </div>
  );
}
